/* ArrivalSIM hero destination search (concept, 2026-10-19).
   Reuses the destinations search index (collections/destinations?view=search-index + snippets/dest-aliases)
   and the same sessionStorage cache as snippets/dest-search.liquid.
   Core matching is pure and exported for node tests (module.exports). */
(function (root) {
  'use strict';

  var norm = function (s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\u00df/g, 'ss').replace(/\u0131/g, 'i').replace(/[^a-z0-9]+/g, ' ').trim();
  };

  /* Optimal string alignment distance (Levenshtein + adjacent swaps), stops early above max. */
  var dist = function (a, b, max) {
    if (a === b) return 0;
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    var p2 = null, p = [], c, i, j;
    for (j = 0; j <= lb; j++) p[j] = j;
    for (i = 1; i <= la; i++) {
      c = [i]; var rowMin = i;
      for (j = 1; j <= lb; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        var v = Math.min(p[j] + 1, c[j - 1] + 1, p[j - 1] + cost);
        if (p2 && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, p2[j - 2] + 1);
        c[j] = v; if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      p2 = p; p = c;
    }
    return p[lb];
  };

  /* Countries that are only sold inside a regional eSIM (listed in custom.region_coverage, no own product). */
  var NAMES = { TW: ['Taiwan', 'Taiwan'], PR: ['Puerto Rico', 'Puerto Rico'], MC: ['Monaco', 'Monaco'], ML: ['Mali', 'Mali'],
    LR: ['Liberia', 'Liberia'], SV: ['El Salvador', 'El Salvador'], CF: ['Central African Republic', 'Centraal-Afrikaanse Republiek'] };
  var regionName = function (cc, lang) {
    if (NAMES[cc]) return NAMES[cc][lang === 'nl' ? 1 : 0];
    try { return new Intl.DisplayNames([lang], { type: 'region' }).of(cc) || cc; } catch (e) { return cc; }
  };
  var lex = function (it, t, handle, al) {
    var all = [t, handle, al].join(' ').trim();
    it._hsT = t; it._hsH = handle; it._hsP = ' ' + all + ' '; it._hsW = all.split(' ');
  };

  var prep = function (idx) {
    if (idx._hs) return idx;
    var aliases = idx.aliases || {}, own = {}, regions = [];
    idx.items.forEach(function (it) {
      lex(it, norm(it.t), norm(String(it.h || '').replace(/-esim$/, '')), norm(aliases[it.c || it.r] || ''));
      if (it.c) own[it.c] = 1;
      if (it.r) regions.push(it);
    });
    var m = String((idx.items[0] || {}).u || '').match(/^\/([a-z]{2})(-[a-z]{2})?\//i), lang = m ? m[1].toLowerCase() : 'en';
    var virt = {};
    regions.forEach(function (r) {
      (r.cov || []).forEach(function (cc) {
        if (own[cc]) return;
        var v = virt[cc];
        if (!v) {
          v = virt[cc] = { c: cc, r: '', t: regionName(cc, lang), regs: [], virtual: true };
          lex(v, norm(v.t), '', norm([regionName(cc, 'en'), regionName(cc, 'nl'), aliases[cc] || ''].join(' ')));
        }
        v.regs.push(r);
      });
    });
    idx._hsV = Object.keys(virt).map(function (k) {
      var v = virt[k]; v.regs.sort(function (a, b) { return a.cov.length - b.cov.length || a.b - b.b; }); return v;
    });
    idx._hs = true;
    return idx;
  };

  /* Score one item. 100 exact title, 95 exact handle (EN name), 90 exact name/alias (85 when it is only the first word of the item's own name), 75 title starts with query,
     60 a name/alias word starts with query, 40/30 typo match (1 edit from 4 letters, up to 2 edits from 7 letters), 25 typo in a word start. */
  var score = function (it, q) {
    if (!q) return 0;
    if (it._hsT === q) return 100;
    if (it._hsH === q) return 95;
    if (it._hsP.indexOf(' ' + q + ' ') > -1) {
      /* Only the first word of this item's own multi-word name ("caribisch" in "Caribisch Nederland")
         ranks below an item that has the word as a full name (the Caribbean region). */
      return ((it._hsT + ' ').indexOf(q + ' ') === 0 && it._hsT !== q) || (it._hsH + ' ').indexOf(q + ' ') === 0 ? 85 : 90;
    }
    if (it._hsT.indexOf(q) === 0) return 75;
    if (it._hsP.indexOf(' ' + q) > -1) return 60;
    if (q.length < 4) return 0;
    var max = q.length >= 7 ? 2 : 1, n = q.split(' ').length, w = it._hsW, best = 0;
    for (var i = 0; i + n <= w.length; i++) {
      var term = w.slice(i, i + n).join(' ');
      if (term.length < 3) continue;
      var d = dist(q, term, max);
      if (d <= max) { best = Math.max(best, 50 - d * 10); continue; }
      if (q.length >= 5 && term.length > q.length && dist(q, term.slice(0, q.length), 1) <= 1) best = Math.max(best, 25);
    }
    return best;
  };

  var cmp = function (a, b) {
    return b.s - a.s || (a.it.r ? 1 : 0) - (b.it.r ? 1 : 0) || a.it.b - b.it.b;
  };

  /* Ranked suggestions: countries before regions on equal score, then catalogue order (popularity).
     After a strong country match, regional eSIMs that cover it are added (flagged "incl"). */
  var search = function (idx, query, limit) {
    prep(idx); limit = limit || 6;
    var q = norm(query);
    if (!q) return [];
    var hits = [];
    idx.items.forEach(function (it) { var s = score(it, q); if (s > 0) hits.push({ it: it, s: s }); });
    /* A country without its own product: show the regional eSIMs that cover it (smallest region first, Global last). */
    var strong = hits.some(function (h) { return h.s >= 60; });
    (idx._hsV || []).forEach(function (v) {
      var s = score(v, q); if (!s || (s < 60 && strong)) return;
      v.regs.forEach(function (r, k) {
        var rs = s - 0.01 * (k + 1), have = null;
        hits.forEach(function (h) { if (h.it === r) have = h; });
        if (!have) hits.push({ it: r, s: rs, incl: v, via: v });
        else if (have.s < rs) { have.s = rs; have.incl = v; have.via = v; }
      });
    });
    hits.sort(cmp);
    var out = hits.slice(0, limit);
    var top = out[0];
    if (top && q.length >= 3 && top.s >= 60 && top.it.c && out.length < limit) {
      var regions = idx.items.filter(function (it) {
        return it.r && (it.cov || []).indexOf(top.it.c) > -1 && !out.some(function (h) { return h.it === it; });
      }).sort(function (a, b) { return a.cov.length - b.cov.length || a.b - b.b; });
      regions.slice(0, Math.min(2, limit - out.length)).forEach(function (it) { out.push({ it: it, s: 1, incl: top.it }); });
    }
    return out;
  };

  /* Where a submit without a highlighted suggestion goes. */
  var resolve = function (idx, query, overview) {
    var q = norm(query);
    if (!q || !idx) return { url: overview, why: q ? 'no-index' : 'empty' };
    var hits = search(idx, q, 50).filter(function (h) { return !h.incl || h.via; });
    if (!hits.length) return { url: overview, why: 'unknown' };
    var top = hits[0], next = hits[1];
    if (hits.length === 1 && (q.length >= 2 || top.s >= 90)) return { url: top.it.u, why: 'single', it: top.it };
    if (top.s >= 90) return { url: top.it.u, why: 'exact', it: top.it };
    if (q.length >= 2 && top.s >= 30 && (!next || top.s - next.s >= 10)) return { url: top.it.u, why: 'top', it: top.it };
    /* A country beats regions with the same score ("amerikaa" -> USA, not North/South America). */
    var rivals = hits.filter(function (h) { return h !== top && !h.it.r && top.s - h.s < 10; });
    if (q.length >= 2 && top.s >= 30 && !top.it.r && !rivals.length) return { url: top.it.u, why: 'top-country', it: top.it };
    /* Region-only country: its closest region wins when the other near hits are just its wider regions ("taiwn" -> Asia). */
    if (q.length >= 2 && top.s >= 30 && top.via && !hits.some(function (h) { return h !== top && top.s - h.s < 10 && h.via !== top.via; })) return { url: top.it.u, why: 'region-only', it: top.it };
    return { url: overview, why: 'ambiguous' };
  };

  var core = { regionName: regionName, norm: norm, dist: dist, prep: prep, score: score, search: search, resolve: resolve };
  if (typeof module !== 'undefined' && module.exports) { module.exports = core; return; }
  root.ASHeroSearch = core;

  /* ---------- UI ---------- */
  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; });
  };

  var loadIndex = function (url) {
    var key = 'as_idx:' + url;
    try {
      var c = sessionStorage.getItem(key);
      if (c) { var o = JSON.parse(c); if (o.t > Date.now() - 36e5 && o.d && o.d.items) return Promise.resolve(o.d); }
    } catch (e) {}
    var acc = [], aliases = {};
    var page = function (n) {
      return fetch(url + '&page=' + n, { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (d) {
        acc = acc.concat(d.items || []); if (n === 1) aliases = d.aliases || {};
        return d.page < d.pages && n < 20 ? page(n + 1) : null;
      });
    };
    return page(1).then(function () {
      var idx = { items: acc, aliases: aliases };
      try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: idx })); } catch (e) {}
      return idx;
    });
  };

  var reduced = function () { return root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches; };

  var typer = function (input, examples) {
    var stat = input.getAttribute('placeholder'), timer = null, on = false, ex = 0, ch = 0, del = false;
    var tick = function () {
      if (!on) return;
      if (document.hidden) { timer = setTimeout(tick, 600); return; }
      var word = examples[ex], wait;
      if (!del) {
        ch++; input.setAttribute('placeholder', word.slice(0, ch));
        if (ch >= word.length) { del = true; wait = 1600; } else wait = 95;
      } else {
        ch--; input.setAttribute('placeholder', word.slice(0, ch));
        if (ch <= 0) { del = false; ex = (ex + 1) % examples.length; wait = 380; } else wait = 40;
      }
      timer = setTimeout(tick, wait);
    };
    return {
      start: function () {
        if (on || !examples.length || reduced() || input.value || document.activeElement === input) return;
        on = true; ch = 0; del = false; input.setAttribute('placeholder', ''); timer = setTimeout(tick, 500);
      },
      stop: function () { on = false; clearTimeout(timer); input.setAttribute('placeholder', stat); }
    };
  };

  var init = function (form) {
    if (form._hs) return; form._hs = true;
    var input = form.querySelector('[data-hs-input]'), list = form.querySelector('[data-hs-list]');
    var empty = form.querySelector('[data-hs-empty]'), status = form.querySelector('[data-hs-status]');
    var url = form.getAttribute('data-index'), overview = form.getAttribute('action');
    var t = { from: form.getAttribute('data-from'), incl: form.getAttribute('data-incl'), one: form.getAttribute('data-count-one'), other: form.getAttribute('data-count-other') };
    var idx = null, loading = null, hits = [], active = -1, lastQ = null;

    var examples = (form.getAttribute('data-examples') || '').split('|').map(function (s) { return s.trim(); }).filter(Boolean);
    var ty = typer(input, examples);
    ty.start();
    if (root.matchMedia) {
      var mq = root.matchMedia('(prefers-reduced-motion: reduce)');
      var onMq = function () { if (mq.matches) ty.stop(); else ty.start(); };
      if (mq.addEventListener) mq.addEventListener('change', onMq); else if (mq.addListener) mq.addListener(onMq);
    }

    var load = function () {
      if (idx) return Promise.resolve(idx);
      if (!loading) loading = loadIndex(url).then(function (d) { idx = ASHeroSearch.prep(d); render(); return idx; }).catch(function () { loading = null; return null; });
      return loading;
    };

    var close = function () {
      list.hidden = true; empty.hidden = true; active = -1;
      input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant');
    };

    var setActive = function (i) {
      var opts = list.querySelectorAll('[role="option"]');
      if (!opts.length) return;
      if (i < 0) i = opts.length - 1; if (i >= opts.length) i = 0;
      active = i;
      [].forEach.call(opts, function (o, k) { o.setAttribute('aria-selected', k === i ? 'true' : 'false'); });
      input.setAttribute('aria-activedescendant', opts[i].id);
      opts[i].scrollIntoView && opts[i].scrollIntoView({ block: 'nearest' });
    };

    var render = function () {
      var q = input.value;
      if (!ASHeroSearch.norm(q)) { hits = []; list.innerHTML = ''; close(); status.textContent = ''; lastQ = q; return; }
      if (!idx) { load(); return; }
      if (q === lastQ && !list.hidden) return;
      lastQ = q;
      hits = ASHeroSearch.search(idx, q, 6);
      active = -1; input.removeAttribute('aria-activedescendant');
      list.innerHTML = hits.map(function (h, i) {
        var it = h.it;
        var note = h.incl ? '<span class="hsearch__note">' + esc(t.incl.replace('[country]', h.incl.t)) + '</span>' : '';
        return '<li class="hsearch__opt" role="option" id="' + list.id + '-' + i + '" aria-selected="false" data-url="' + esc(it.u) + '">' +
          '<span class="hsearch__flag" aria-hidden="true">' + esc(it.f) + '</span>' +
          '<span class="hsearch__name">' + esc(it.t) + note + '</span>' +
          '<span class="hsearch__price">' + esc(t.from) + ' <span class="price">' + esc(it.p) + '</span></span></li>';
      }).join('');
      var n = hits.length;
      list.hidden = !n; empty.hidden = !!n;
      input.setAttribute('aria-expanded', n ? 'true' : 'false');
      status.textContent = n ? (n === 1 ? t.one : t.other.replace('[count]', n)) : empty.textContent;
    };

    var go = function (u) { if (u) root.location.href = u; };

    input.addEventListener('focus', function () { ty.stop(); load(); if (input.value) { lastQ = null; render(); } });
    input.addEventListener('blur', function () { setTimeout(function () { if (document.activeElement !== input) { close(); if (!input.value) ty.start(); } }, 150); });
    form.addEventListener('pointerenter', load, { once: true });
    input.addEventListener('input', function () { ty.stop(); render(); });
    input.addEventListener('keydown', function (e) {
      var open = !list.hidden;
      if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) { lastQ = null; render(); } if (!list.hidden) setActive(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); if (open) setActive(active - 1); }
      else if (e.key === 'Escape') { if (open || !empty.hidden) { e.preventDefault(); close(); } else if (input.value) { e.preventDefault(); input.value = ''; render(); } }
      else if (e.key === 'Enter' && open && active > -1 && hits[active]) { e.preventDefault(); go(hits[active].it.u); }
      else if (e.key === 'Tab') close();
    });
    list.addEventListener('mousedown', function (e) { e.preventDefault(); });
    list.addEventListener('click', function (e) { var o = e.target.closest('[role="option"]'); if (o) go(o.getAttribute('data-url')); });
    list.addEventListener('mousemove', function (e) {
      var o = e.target.closest('[role="option"]'); if (!o) return;
      var i = [].indexOf.call(list.querySelectorAll('[role="option"]'), o); if (i !== active) setActive(i);
    });
    document.addEventListener('click', function (e) { if (!form.contains(e.target)) close(); });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!list.hidden && active > -1 && hits[active]) return go(hits[active].it.u);
      var q = input.value;
      if (!ASHeroSearch.norm(q)) return go(overview);
      var timeout = new Promise(function (r) { setTimeout(function () { r(null); }, 2500); });
      Promise.race([load(), timeout]).then(function (d) { go(ASHeroSearch.resolve(d, q, overview).url); });
    });
  };

  var boot = function () { [].forEach.call(document.querySelectorAll('[data-hero-search]'), init); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})(typeof window !== 'undefined' ? window : this);
